import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Heart, Camera } from "lucide-react";
import { EarlyAccessForm } from "./EarlyAccessForm";
import { scrollToSection } from "./SmoothScroll";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  [{ t: "The memories" }],
  [{ t: "stay. " }, { t: "The mess", accent: true }],
  [{ t: "doesn't.", accent: true }],
];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay: 0.25 + i * 0.13, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="relative mx-auto w-full max-w-[460px] [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92, rotate: -6 }}
        animate={{ opacity: 1, scale: 1, rotate: -2.5 }}
        transition={{ duration: 1.3, delay: 0.35, ease: EASE }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative"
      >
        <div className="absolute -inset-3 rounded-[2.6rem] bg-teal rotate-[6deg] opacity-90" aria-hidden="true" />
        <div className="relative rounded-[2.4rem] bg-white p-3 shadow-[0_40px_90px_-30px_rgba(18,52,90,0.55)]">
          <div className="hero-clip overflow-hidden rounded-[1.9rem]">
            <motion.img
              src="/images/hero.webp"
              alt="A smiling child proudly holding up a colorful crayon drawing of a sun and a house"
              width="900"
              height="1341"
              fetchpriority="high"
              initial={{ scale: 1.25 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.8, delay: 0.35, ease: EASE }}
              className="aspect-[4/5] w-full object-cover object-[50%_35%]"
            />
          </div>
        </div>

        <motion.div
          style={{ translateZ: 70 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="absolute -left-4 bottom-10 sm:-left-12 w-[210px] rounded-2xl bg-cream p-3.5 shadow-[0_24px_50px_-18px_rgba(18,52,90,0.45)] rotate-[-4deg]"
        >
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-teal-dark">
            <Heart className="h-3 w-3 fill-coral text-coral" aria-hidden="true" /> Memory saved
          </p>
          <p className="mt-1.5 font-heading text-[15px] font-extrabold leading-snug text-navy">“Our sunny house,” age 6</p>
          <p className="mt-1 text-xs text-navy/70">Sample memory</p>
        </motion.div>

        <motion.div
          style={{ translateZ: 110 }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
          className="absolute -right-3 top-8 sm:-right-8 flex h-20 w-20 items-center justify-center rounded-full bg-gold text-navy shadow-xl"
        >
          <Camera className="h-8 w-8" aria-hidden="true" />
          <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full animate-[spin_16s_linear_infinite]" aria-hidden="true">
            <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
            <text className="fill-navy text-[10.5px] font-bold tracking-[0.2em] uppercase"><textPath href="#circ">snap · save · keep ·</textPath></text>
          </svg>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -160]);

  return (
    <section id="top" ref={ref} aria-labelledby="hero-heading" className="relative overflow-hidden pt-28 pb-20 sm:pt-36 lg:pt-40 lg:pb-28">
      <motion.div style={{ y: yBlob }} aria-hidden="true" className="pointer-events-none absolute -right-40 -top-24 h-[520px] w-[520px] rounded-full bg-gold/30 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -left-32 bottom-0 h-[380px] w-[380px] rounded-full bg-coral/25 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10">
        <div>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="eyebrow flex items-center gap-3">
            <span className="h-px w-8 bg-teal" aria-hidden="true" /> Their childhood is worth keeping
          </motion.p>
          <h1 id="hero-heading" data-testid="hero-headline" className="mt-6 font-heading font-black tracking-[-0.035em] text-navy text-[3.1rem] leading-[0.95] sm:text-7xl lg:text-[5.6rem] xl:text-[6.3rem]">
            {LINES.map((line, i) => (
              <MaskLine key={i} i={i}>
                {line.map((w, j) => (
                  <span key={j} className={w.accent ? "text-teal italic" : ""}>{w.t}</span>
                ))}
              </MaskLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="mt-8 max-w-lg text-base sm:text-lg leading-relaxed text-navy/80"
          >
            Photograph the artwork. Save the story. Keep the moments that matter—without keeping every piece of paper.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-10 max-w-xl"
            data-signup-zone
          >
            <EarlyAccessForm source="hero" />
            <a href="#how-it-works" data-testid="hero-secondary-link" onClick={(e) => { e.preventDefault(); scrollToSection("how-it-works"); }} className="nav-link mt-6 inline-block font-heading font-bold text-navy">
              See how it works
            </a>
          </motion.div>
        </div>

        <motion.div style={{ y: yVisual }}>
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
};
